import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Tooltip from '../components/Tooltip.js';
import CandleStickChart from './CandlestickChart.js';
import IndicatorSelector from './IndicatorSelector.js';
import ActionResult from '../components/ActionResult.js';
import StickySettings from '../helpers/StickySettings.js';
import RichTable from '../components/richTable/RichTable.jsx';
import tableHelpers from '../helpers/TableHelpers.js';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import * as ss from 'simple-statistics';
import { Chart } from "react-google-charts";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getLocalDate } from '../helpers/DateHelpers.js';

function StockResearch(props) {

    const [symbols, setSymbols] = useState([]);
    const [symbol, setSymbol] = useState(props.symbol || StickySettings.getString('stockResearchSymbol', null));
    const [comparisonSymbol, setComparisonSymbol] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        if (props.startDate) {
            return props.startDate instanceof Date ? props.startDate : getLocalDate(props.startDate);
        }
        return new Date(2014, 0, 1);
    });
    const [endDate, setEndDate] = useState(() => {
        if (props.endDate) {
            return props.endDate instanceof Date ? props.endDate : getLocalDate(props.endDate);
        }
        return new Date();
    });
    const [indicatorOptions, setIndicatorOptions] = useState([]);
    const [selectedIndicators, setSelectedIndicators] = useState(StickySettings.getJSON('stockResearchIndicators', []));
    const [clickedDates, setClickedDates] = useState(() => {
        if (props.annotations && Array.isArray(props.annotations)) {
            return props.annotations;
        }
        return [];
    });

    useEffect(() => {
        if (props.symbol) {
            setSymbol(props.symbol);
        }
    }, [props.symbol]);

    useEffect(() => {
        if (props.startDate) {
            setStartDate(props.startDate instanceof Date ? props.startDate : getLocalDate(props.startDate));
        }
    }, [props.startDate]);

    useEffect(() => {
        if (props.endDate) {
            setEndDate(props.endDate instanceof Date ? props.endDate : getLocalDate(props.endDate));
        }
    }, [props.endDate]);

    useEffect(() => {
        if (props.annotations && Array.isArray(props.annotations)) {
            setClickedDates(props.annotations);
        }
    }, [props.annotations]);
    const [quoteData, setQuoteData] = useState([]);
    const [quoteMetaData, setQuoteMetaData] = useState([]);
    const [searchConditions, setSearchConditions] = useState([]);
    const [searchIndicatorToAdd, setSearchIndicatorToAdd] = useState('');
    const [timeframe, setTimeframe] = useState(1);
    const [correlationResults, setCorrelationResults] = useState(null);
    const [correlationTarget, setCorrelationTarget] = useState('oneWeekReturn');
    const [similarityThreshold, setSimilarityThreshold] = useState(0.90);
    const [patterns, setPatterns] = useState([]);
    const [excludeDuplicates, setExcludeDuplicates] = useState(true);
    const [excludeDuplicatesPeriod, setExcludeDuplicatesPeriod] = useState('oneWeekReturn');
    const [chartIndicator, setChartIndicator] = useState('');
    const [searchOperatorToAdd, setSearchOperatorToAdd] = useState('<');
    const [searchValueToAdd, setSearchValueToAdd] = useState('');
    const [searchIndicator1ToAdd, setSearchIndicator1ToAdd] = useState('');
    const [searchIndicator2ToAdd, setSearchIndicator2ToAdd] = useState('');
    const [searchIndicatorOperatorToAdd, setSearchIndicatorOperatorToAdd] = useState('<');
    const [simBalance, setSimBalance] = useState(10000);
    const [simPosition, setSimPosition] = useState(0);
    const [smartSearchThreshold, setSmartSearchThreshold] = useState(1.5);
    const [simQuantity, setSimQuantity] = useState('');
    const [simTransactions, setSimTransactions] = useState([]);
    const [showVolume, setShowVolume] = useState(StickySettings.getBool('stockResearchShowVolume', false));
    const [simAvgPrice, setSimAvgPrice] = useState(0);
    const [tradeSignals, setTradeSignals] = useState([]);
    const [isFooterExpanded, setIsFooterExpanded] = useState(StickySettings.getBool('stockResearchFooterExpanded', true));
    const [correlationStdDevThreshold, setCorrelationStdDevThreshold] = useState(0.5);
    const [savedSettings, setSavedSettings] = useState([]);
    const [settingName, setSettingName] = useState('');
    const [actionResult, setActionResult] = useState(null);
    const [lowerIndicatorSma, setLowerIndicatorSma] = useState(StickySettings.getString('stockResearchLowerSma', ''));

    useEffect(() => {
        StickySettings.setJSON('stockResearchIndicators', selectedIndicators.filter(i => i.type !== 'algobuysignals'));

        setSearchConditions(prev => {
            if (!Array.isArray(prev)) return [];
            const validIds = new Set(selectedIndicators.map(i => i.id));
            return prev.filter(c => validIds.has(c.indicatorId));
        });
    }, [selectedIndicators]);

    useEffect(() => {
        StickySettings.setBool('stockResearchShowVolume', showVolume);
    }, [showVolume]);

    useEffect(() => {
        StickySettings.setBool('stockResearchFooterExpanded', isFooterExpanded);
    }, [isFooterExpanded]);

    useEffect(() => {
        StickySettings.setString('stockResearchLowerSma', lowerIndicatorSma);
    }, [lowerIndicatorSma]);

    useEffect(() => {
        if (props.algoId) {
            const id = 'algobuysignals' + props.algoId;
            setSelectedIndicators(prev => {
                if (prev.some(i => i.id === id)) return prev;
                return [...prev, {
                    type: 'algobuysignals',
                    id: id,
                    location: 'lower',
                    config: { algoId: props.algoId }
                }];
            });
            setSearchConditions(prev => {
                if (prev.some(c => c.indicatorId === id)) return prev;
                return [...prev, {
                    type: 'value',
                    indicatorId: id,
                    operator: '<>',
                    value: '0',
                    uid: Date.now() + Math.random()
                }];
            });
        }
    }, [props.algoId]);

    const fetchSavedSettings = useCallback(async () => {
        try {
            const res = await fetch(`/api/chartSettings?userId=${props.userId}`);
            const data = await res.json();
            if (data.success) {
                setSavedSettings(data.settings);
            }
        } catch (e) {
            console.error(e);
        }
    }, [props.userId]);

    useEffect(() => {
        if (props.userId) {
            fetchSavedSettings();
        }
    }, [props.userId, fetchSavedSettings]);

    async function saveSetting() {
        if (!settingName) return alert("Please enter a name");
        try {
            const indicatorsToSave = selectedIndicators.filter(i => i.type !== 'algobuysignals');
            let res = await fetch(`/api/chartSettingsSave?userId=${props.userId}&name=${settingName}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ indicators: indicatorsToSave })
            });
            let result = await res.json();
            setActionResult(result);
            fetchSavedSettings();
        } catch (e) {
            console.error(e);
            setActionResult({ error: e.message });
        }
    }

    async function deleteSetting() {
        if (!settingName) return;
        if (!window.confirm(`Delete setting ${settingName}?`)) return;
        try {
            let res = await fetch(`/api/chartSettingsDelete?userId=${props.userId}&name=${settingName}`,
                { method: 'POST' }
            );
            let result = await res.json();
            setActionResult(result);
            setSettingName('');
            fetchSavedSettings();
        } catch (e) {
            console.error(e);
            setActionResult({ error: e.message });
        }
    }

    function loadSetting(e) {
        const name = e.target.value;
        setSettingName(name);
        if (name) {
            const setting = savedSettings.find(s => s.name === name);
            if (setting && setting.indicators) {
                let inds = setting.indicators;
                if (typeof inds === 'string') {
                    try {
                        inds = JSON.parse(inds);
                    } catch (e) { console.error("Error parsing indicators", e); }
                }
                setSelectedIndicators(inds);
            }
        }
    }

    function getIndicatorValue(quote, indicatorId) {
        if (!quote) return null;
        let val = undefined;
        if (quote.upper && quote.upper[indicatorId] !== undefined) val = quote.upper[indicatorId];
        else if (quote.lower && quote.lower[indicatorId] !== undefined) val = quote.lower[indicatorId];

        if (val !== undefined && val !== null) {
            if (typeof val === 'object') {
                if (val.value !== undefined) val = val.value;
                else if (val.k !== undefined) val = val.k;
                else val = Object.values(val)[0];
            }
            return val;
        }
        return null;
    }

    const { quoteDateMap, quoteDatesArray } = useMemo(() => {
        const map = new Map();
        const dates = [];
        if (quoteData && quoteData.length > 0) {
            quoteData.forEach((q, idx) => {
                const d = getLocalDate(q.date);
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                map.set(key, { quote: q, index: idx, dateObj: d, time: d.getTime() });
                dates.push(d.getTime());
            });
        }
        return { quoteDateMap: map, quoteDatesArray: dates };
    }, [quoteData]);

    const findQuoteForDate = useCallback((targetDate, fromIndex = 0) => {
        if (!quoteData || quoteData.length === 0) return null;
        const targetTime = targetDate instanceof Date ? targetDate.getTime() : new Date(targetDate).getTime();
        const start = Math.max(0, fromIndex);
        for (let i = start; i < quoteDatesArray.length; i++) {
            if (quoteDatesArray[i] >= targetTime) {
                return quoteData[i];
            }
        }
        return null;
    }, [quoteData, quoteDatesArray]);

    const calculateReturn = useCallback((startPrice, endPrice) => {
        if (startPrice && endPrice) {
            return (endPrice - startPrice) / startPrice * 100;
        }
        return null;
    }, []);

    const calculateMaxDrawdown = useCallback((startIndex, targetDate, entryPrice) => {
        if (!quoteData || startIndex >= quoteData.length - 1) return 0;
        const targetTime = targetDate instanceof Date ? targetDate.getTime() : new Date(targetDate).getTime();
        let minLow = entryPrice;
        for (let i = startIndex + 1; i < quoteData.length; i++) {
            if (quoteDatesArray[i] > targetTime) break;
            const q = quoteData[i];
            if (q.low < minLow) minLow = q.low;
        }
        return (minLow - entryPrice) / entryPrice * 100;
    }, [quoteData, quoteDatesArray]);

    const analyzeCorrelations = useCallback((isAutoUpdate) => {
        const isAuto = isAutoUpdate === true;
        if (!quoteData || quoteData.length === 0) return;

        let currentQuote = null;
        for (let i = quoteData.length - 1; i >= 0; i--) {
            if (getLocalDate(quoteData[i].date) <= endDate) {
                currentQuote = quoteData[i];
                break;
            }
        }

        if (clickedDates.length < 2) {
            if (!isAuto) alert("Please select at least 2 dates from the chart/table to analyze.");
            else setCorrelationResults(null);
            return;
        }

        const indicators = selectedIndicators.filter(i => i.location === 'lower').map(i => i.id);
        if (indicators.length === 0) {
            if (!isAuto) alert("Please select at least 1 indicator to analyze.");
            else setCorrelationResults(null);
            return;
        }

        const getReturn = (row, target, startIndex) => {
            const d = getLocalDate(row.date);
            let targetDate = new Date(d);
            if (target === 'oneDayReturn') targetDate.setDate(d.getDate() + 1);
            else if (target === 'oneWeekReturn') targetDate.setDate(d.getDate() + 7);
            else if (target === 'oneMonthReturn') targetDate.setMonth(d.getMonth() + 1);
            else if (target === 'threeMonthReturn') targetDate.setMonth(d.getMonth() + 3);

            const futureQuote = findQuoteForDate(targetDate, startIndex);
            return calculateReturn(row.close, futureQuote?.close);
        };

        const returnValues = [];

        clickedDates.forEach(clicked => {
            const d = getLocalDate(clicked.date);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const entry = quoteDateMap.get(key);

            if (entry) {
                const row = entry.quote;
                const ret = getReturn(row, correlationTarget, entry.index);
                if (ret !== null && !isNaN(ret)) {
                    const point = { ret: ret };
                    let hasIndicators = false;
                    indicators.forEach(id => {
                        let val = undefined;
                        if (row.upper && row.upper[id] !== undefined) val = row.upper[id];
                        else if (row.lower && row.lower[id] !== undefined) val = row.lower[id];

                        if (val !== undefined && val !== null) {
                            if (typeof val === 'object') {
                                if (val.value !== undefined) val = val.value;
                                else if (val.k !== undefined) val = val.k;
                                else val = Object.values(val)[0];
                            }
                            if (!isNaN(val)) {
                                point[id] = val;
                                hasIndicators = true;
                            }
                        }
                    });
                    if (hasIndicators) returnValues.push(point);
                }
            }
        });

        if (returnValues.length < 2) {
            if (!isAuto) alert("Not enough data points with valid future returns to analyze.");
            else setCorrelationResults(null);
            return;
        }

        const results = [];
        indicators.forEach(id => {
            const allV1 = [];
            const allV2 = [];
            const winV1 = [];
            const winV2 = [];
            const lossV1 = [];
            const lossV2 = [];

            returnValues.forEach(p => {
                if (p[id] !== undefined) {
                    allV1.push(p[id]);
                    allV2.push(p.ret);

                    if (p.ret > 0) {
                        winV1.push(p[id]);
                        winV2.push(p.ret);
                    } else if (p.ret < 0) {
                        lossV1.push(p[id]);
                        lossV2.push(p.ret);
                    }
                }
            });

            let overallCorr = null;
            if (allV1.length > 1) {
                try {
                    const corr = ss.sampleCorrelation(allV1, allV2);
                    if (!isNaN(corr)) {
                        overallCorr = corr;
                    }
                } catch (e) { }
            }

            let winCorr = null;
            let lossCorr = null;

            if (winV1.length > 1) {
                try {
                    const corr = ss.sampleCorrelation(winV1, winV2);
                    if (!isNaN(corr)) {
                        winCorr = corr;
                    }
                } catch (e) { }
            }

            if (lossV1.length > 1) {
                try {
                    const corr = ss.sampleCorrelation(lossV1, lossV2);
                    if (!isNaN(corr)) {
                        lossCorr = corr;
                    }
                } catch (e) { }
            }

            let winAvg = null;
            if (winV1.length > 0) {
                winAvg = winV1.reduce((a, b) => a + b, 0) / winV1.length;
            }

            let lossAvg = null;
            if (lossV1.length > 0) {
                lossAvg = lossV1.reduce((a, b) => a + b, 0) / lossV1.length;
            }

            let overallAvg = null;
            if (allV1.length > 0) {
                overallAvg = allV1.reduce((a, b) => a + b, 0) / allV1.length;
            }

            let stdDev = 0;
            if (allV1.length > 1) {
                try {
                    stdDev = ss.standardDeviation(allV1);
                } catch (e) { }
            }

            let currentValue = null;
            if (currentQuote) {
                let val = undefined;
                if (currentQuote.upper && currentQuote.upper[id] !== undefined) val = currentQuote.upper[id];
                else if (currentQuote.lower && currentQuote.lower[id] !== undefined) val = currentQuote.lower[id];

                if (val !== undefined && val !== null) {
                    if (typeof val === 'object') {
                        if (val.value !== undefined) val = val.value;
                        else if (val.k !== undefined) val = val.k;
                        else val = Object.values(val)[0];
                    }
                    if (!isNaN(val)) currentValue = val;
                }
            }

            if (winCorr !== null || lossCorr !== null || overallCorr !== null) {
                results.push({
                    indicator: id,
                    overallCorrelation: overallCorr,
                    winCorrelation: winCorr,
                    lossCorrelation: lossCorr,
                    winCount: winV1.length,
                    lossCount: lossV1.length,
                    winAvg: winAvg,
                    lossAvg: lossAvg,
                    overallAvg: overallAvg,
                    stdDev: stdDev,
                    currentValue: currentValue
                });
            }
        });

        results.sort((a, b) => {
            const valA = a.winCorrelation !== null ? a.winCorrelation : -2;
            const valB = b.winCorrelation !== null ? b.winCorrelation : -2;
            return valB - valA;
        });
        setCorrelationResults(results);
    }, [quoteData, clickedDates, selectedIndicators, correlationTarget, findQuoteForDate, calculateReturn, endDate, quoteDateMap]);

    const hasCorrelationsRef = useRef(false);
    useEffect(() => {
        if (correlationResults && correlationResults.length > 0) {
            hasCorrelationsRef.current = true;
        }
    }, [correlationResults]);

    useEffect(() => {
        if (hasCorrelationsRef.current && clickedDates.length >= 2) {
            analyzeCorrelations(true);
        }
    }, [clickedDates, analyzeCorrelations]);

    useEffect(() => {
        (async function () {

            const syms = await (await fetch(`/api/strategySymbols`)).json();
            setSymbols(syms);

            const options = await (await fetch(`/api/indicators`)).json();
            setIndicatorOptions(options.indicators || []);
            setPatterns(options.patterns || []);

        })();
    }, []);

    useEffect(() => {
        setClickedDates(props.annotations && Array.isArray(props.annotations) ? props.annotations : []);
        resetSim();
        setQuoteData([]);
        setQuoteMetaData([]);
        setTradeSignals([]);
        if (symbol) StickySettings.setString('stockResearchSymbol', symbol);
    }, [symbol, props.annotations]);

    useEffect(() => {
        setTradeSignals([]);
    }, [endDate]);

    useEffect(() => {
        (async function () {
            if (symbol) {
                let payload = { indicators: selectedIndicators };
                let apiSymbol = comparisonSymbol ? `${symbol} / ${comparisonSymbol}` : symbol;
                let url = `/api/quoteHistoryWithIndicators?symbol=` + encodeURIComponent(apiSymbol) +
                    "&startDate=" + startDate.toLocaleDateString() +
                    "&endDate=" + new Date().toLocaleDateString();

                if (timeframe === 2) url += "&timeframe=2";

                let response = await fetch(url, { method: 'POST', body: JSON.stringify(payload) });
                let result = await response.json();
                console.log("quoteHistoryWithIndicators", result);
                if (result && result.data) {
                    let data = result.data.sort((a, b) => getLocalDate(a.date) - getLocalDate(b.date));
                    setQuoteData(data);
                    if (result.metaData && result.metaData.indicators) {
                        setQuoteMetaData(result.metaData.indicators);
                    }
                }
            }
        })();
    }, [symbol, comparisonSymbol, selectedIndicators, timeframe, startDate]);

    function adjustDate(months, days) {
        if (days !== 0 && quoteData && quoteData.length > 0) {
            let currentIndex = -1;
            for (let i = 0; i < quoteData.length; i++) {
                if (getLocalDate(quoteData[i].date) <= endDate) {
                    currentIndex = i;
                } else {
                    break;
                }
            }

            if (currentIndex === -1) {
                if (days > 0) {
                    setEndDate(getLocalDate(quoteData[0].date));
                } else {
                    let d = new Date(endDate);
                    d.setDate(d.getDate() + days);
                    setEndDate(d);
                }
                return;
            }

            let newIndex = currentIndex + days;
            if (newIndex >= quoteData.length) newIndex = quoteData.length - 1;
            if (newIndex < 0) newIndex = 0;

            console.log("Setting date to " + quoteData[newIndex].date);
            setEndDate(getLocalDate(quoteData[newIndex].date));
            console.log("Date translated to " + getLocalDate(quoteData[newIndex].date));
        } else {
            let d = new Date(endDate);
            d.setMonth(d.getMonth() + months);
            d.setDate(d.getDate() + days);

            const now = new Date();
            if (d > now) {
                d = now;
            }
            setEndDate(d);
        }
    }

    function handleDateClicked(date) {
        console.log("Date clicked", date);
        //setClickedDates(prev => [...prev, { date: date }]);
    }

    function handleClearDates() {
        setClickedDates([]);
        setCorrelationResults(null);
    }

    function handleRemoveDate(date) {
        const timeToRemove = getLocalDate(date).getTime();
        setClickedDates(prev => prev.filter(d => getLocalDate(d.date).getTime() !== timeToRemove));
    }

    const getCurrentSimPrice = useCallback(() => {
        if (!quoteData || quoteData.length === 0) return 0;
        for (let i = quoteData.length - 1; i >= 0; i--) {
            if (getLocalDate(quoteData[i].date) <= endDate) {
                return quoteData[i].close;
            }
        }
        return 0;
    }, [quoteData, endDate]);

    useEffect(() => {
        const price = getCurrentSimPrice();
        if (simPosition === 0) {
            if (price > 0) {
                setSimQuantity(Math.floor(simBalance / price));
            } else {
                setSimQuantity(0);
            }
        } else {
            setSimQuantity(Math.abs(simPosition));
        }
    }, [simPosition, simBalance, getCurrentSimPrice]);

    function handleSimBuy() {
        const currentPrice = getCurrentSimPrice();
        if (!currentPrice) return;

        let qty = parseInt(simQuantity);
        if (isNaN(qty) || qty < 0) qty = 0;

        const date = endDate.toISOString().split('T')[0];

        if (simPosition < 0) {
            // Cover
            const sharesToCover = qty > 0 ? Math.min(qty, Math.abs(simPosition)) : Math.abs(simPosition);
            const cost = sharesToCover * currentPrice;
            setSimBalance(prev => prev - cost);
            setSimPosition(prev => prev + sharesToCover);
            if (sharesToCover === Math.abs(simPosition)) setSimAvgPrice(0);
            setSimTransactions(prev => [{ date, action: 'Cover', quantity: sharesToCover, price: currentPrice }, ...prev]);
        } else if (simPosition >= 0) {
            // Buy
            const maxShares = Math.floor(simBalance / currentPrice);
            const sharesToBuy = qty > 0 ? qty : maxShares;
            if (sharesToBuy > 0) {
                setSimBalance(prev => prev - (sharesToBuy * currentPrice));
                setSimAvgPrice(prevAvg => {
                    const totalValue = (simPosition * prevAvg) + (sharesToBuy * currentPrice);
                    const totalShares = simPosition + sharesToBuy;
                    return totalValue / totalShares;
                });
                setSimPosition(prev => prev + sharesToBuy);
                setSimTransactions(prev => [{ date, action: 'Buy', quantity: sharesToBuy, price: currentPrice }, ...prev]);
            }
        }
    }

    function handleSimSell() {
        const currentPrice = getCurrentSimPrice();
        if (!currentPrice) return;

        let qty = parseInt(simQuantity);
        if (isNaN(qty) || qty < 0) qty = 0;

        const date = endDate.toISOString().split('T')[0];

        if (simPosition > 0) {
            // Sell
            const sharesToSell = qty > 0 ? Math.min(qty, simPosition) : simPosition;
            const proceeds = sharesToSell * currentPrice;
            setSimBalance(prev => prev + proceeds);
            setSimPosition(prev => prev - sharesToSell);
            if (sharesToSell === simPosition) setSimAvgPrice(0);
            setSimTransactions(prev => [{ date, action: 'Sell', quantity: sharesToSell, price: currentPrice }, ...prev]);
        } else if (simPosition === 0) {
            // Short
            const maxShares = Math.floor(simBalance / currentPrice);
            const sharesToShort = qty > 0 ? qty : maxShares;
            if (sharesToShort > 0) {
                setSimBalance(prev => prev + (sharesToShort * currentPrice));
                setSimPosition(-sharesToShort);
                setSimAvgPrice(currentPrice);
                setSimTransactions(prev => [{ date, action: 'Short', quantity: sharesToShort, price: currentPrice }, ...prev]);
            }
        }
    }

    function resetSim() {
        setSimBalance(10000);
        setSimPosition(0);
        setSimTransactions([]);
        setSimAvgPrice(0);
    }

    function handleRandomDate() {
        const start = startDate.getTime();
        const end = new Date().getTime();
        const randomTime = start + Math.random() * (end - start);
        setEndDate(new Date(randomTime));
    }

    async function handleGetSignals() {
        if (!symbol) return;
        const dateString = endDate.getFullYear() + "-" + (endDate.getMonth() + 1) + "-" + endDate.getDate()

        const url = `/api/backtestTradesOnDate?symbol=${symbol}&date=${dateString}`;
        const response = await fetch(url);
        const data = await response.json();
        setTradeSignals(data);
    }

    function handleCopyCSV() {
        if (!tableData || tableData.length === 0) return;

        let csvContent = "";

        // Headers
        csvContent += mappers.map(m => `"${m.title}"`).join(",") + "\n";

        // Rows
        tableData.forEach(row => {
            let rowString = mappers.map(m => {
                if (m.field === 'date') {
                    if (row.date === 'Average') return 'Average';
                    return tableHelpers.dateFormmatter(row.date, row);
                }

                let val = row[m.field];
                if (m.formatter) {
                    let formatted = m.formatter(val, row);
                    if (typeof formatted === 'string') {
                        return `"${formatted.replace(/"/g, '""')}"`;
                    }
                    return formatted;
                }
                return val;
            }).join(",");
            csvContent += rowString + "\n";
        });

        navigator.clipboard.writeText(csvContent);
    }

    function addSearchCondition() {
        if (searchIndicatorToAdd && searchValueToAdd) {
            setSearchConditions(prev => [...prev, { type: 'value', indicatorId: searchIndicatorToAdd, operator: searchOperatorToAdd, value: searchValueToAdd, uid: Date.now() + Math.random() }]);
            setSearchIndicatorToAdd('');
            setSearchValueToAdd('');
        }
    }

    function addIndicatorCompareCondition() {
        if (searchIndicator1ToAdd && searchIndicator2ToAdd) {
            setSearchConditions(prev => [...prev, { type: 'indicator', indicator1Id: searchIndicator1ToAdd, operator: searchIndicatorOperatorToAdd, indicator2Id: searchIndicator2ToAdd, uid: Date.now() + Math.random() }]);
            setSearchIndicator1ToAdd('');
            setSearchIndicator2ToAdd('');
        }
    }

    function removeSearchCondition(uid) {
        setSearchConditions(prev => prev.filter(c => c.uid !== uid));
    }

    function updateSearchCondition(uid, field, newValue) {
        setSearchConditions(prev => prev.map(c => c.uid === uid ? { ...c, [field]: newValue } : c));
    }

    function runSearch() {
        console.log("runSearch started. quoteData length:", quoteData?.length, "searchConditions:", searchConditions);
        if (!quoteData || quoteData.length === 0) return;

        const conditions = [];
        searchConditions.forEach(item => {
            const type = item.type || 'value';
            if (type === 'value' && item.value !== '' && item.value !== null && item.value !== undefined && item.operator !== '') {
                conditions.push({
                    type: 'value',
                    id: item.indicatorId,
                    operator: item.operator,
                    value: parseFloat(item.value)
                });
            } else if (type === 'indicator' && item.indicator1Id && item.indicator2Id && item.operator) {
                conditions.push({
                    type: 'indicator',
                    indicator1Id: item.indicator1Id,
                    operator: item.operator,
                    indicator2Id: item.indicator2Id,
                });
            }
        });

        console.log("Parsed conditions:", conditions);
        if (conditions.length === 0) return;

        const newDates = [];
        let lastAddedDate = null;
        let nullValueCount = 0;
        let failedOperatorCount = 0;
        let duplicateExcludedCount = 0;

        quoteData.forEach((day, index) => {
            let match = true;
            for (const cond of conditions) {
                if (cond.type === 'value') {
                    let val = getIndicatorValue(day, cond.id);

                    if (val === null) {
                        nullValueCount++;
                        if (index < 5) console.log(`Day ${day.date}: Indicator ${cond.id} is null`);
                        match = false;
                        break;
                    }

                    let opMatch = true;
                    switch (cond.operator) {
                        case '<': if (!(val < cond.value)) opMatch = false; break;
                        case '>': if (!(val > cond.value)) opMatch = false; break;
                        case '<=': if (!(val <= cond.value)) opMatch = false; break;
                        case '>=': if (!(val >= cond.value)) opMatch = false; break;
                        case '<>': if (!(val !== cond.value)) opMatch = false; break;
                        case '=':
                        case '==': if (!(Math.abs(val - cond.value) < 0.01)) opMatch = false; break;
                        default: opMatch = false;
                    }
                    if (!opMatch) {
                        failedOperatorCount++;
                        match = false;
                        if (index < 5) console.log(`Day ${day.date}: Failed cond ${cond.id} ${cond.operator} ${cond.value} (val was ${val})`);
                    }
                } else if (cond.type === 'indicator') {
                    let val1 = getIndicatorValue(day, cond.indicator1Id);
                    let val2 = getIndicatorValue(day, cond.indicator2Id);

                    if (val1 === null || val2 === null) {
                        nullValueCount++;
                        if (index < 5) console.log(`Day ${day.date}: Indicator ${cond.indicator1Id} or ${cond.indicator2Id} is null (val1: ${val1}, val2: ${val2})`);
                        match = false;
                        break;
                    }

                    let opMatch = true;
                    switch (cond.operator) {
                        case '<': if (!(val1 < val2)) opMatch = false; break;
                        case '>': if (!(val1 > val2)) opMatch = false; break;
                        case '<=': if (!(val1 <= val2)) opMatch = false; break;
                        case '>=': if (!(val1 >= val2)) opMatch = false; break;
                        case '<>': if (!(val1 !== val2)) opMatch = false; break;
                        case '=':
                        case '==': if (!(Math.abs(val1 - val2) < 0.01)) opMatch = false; break;
                        default: opMatch = false;
                    }
                    if (!opMatch) {
                        failedOperatorCount++;
                        match = false;
                        if (index < 5) console.log(`Day ${day.date}: Failed cond ${cond.indicator1Id} ${cond.operator} ${cond.indicator2Id} (val1: ${val1}, val2: ${val2})`);
                    }
                }
                if (!match) break;
            }

            if (match) {
                if (excludeDuplicates && lastAddedDate) {
                    const currentDate = getLocalDate(day.date);
                    const prevDate = getLocalDate(lastAddedDate);
                    let cutoffDate = new Date(prevDate);

                    if (excludeDuplicatesPeriod === 'oneDayReturn') cutoffDate.setDate(prevDate.getDate() + 1);
                    else if (excludeDuplicatesPeriod === 'oneWeekReturn') cutoffDate.setDate(prevDate.getDate() + 7);
                    else if (excludeDuplicatesPeriod === 'oneMonthReturn') cutoffDate.setMonth(prevDate.getMonth() + 1);
                    else if (excludeDuplicatesPeriod === 'threeMonthReturn') cutoffDate.setMonth(prevDate.getMonth() + 3);

                    if (currentDate < cutoffDate) {
                        duplicateExcludedCount++;
                        if (index < 5) console.log(`Day ${day.date}: Excluded as duplicate`);
                        return;
                    }
                }

                if (index < 5) console.log(`Day ${day.date}: Match added!`);
                newDates.push({ date: day.date });
                lastAddedDate = day.date;
            }
        });

        console.log(`runSearch complete. Checked ${quoteData.length} days. Null values: ${nullValueCount}, Failed operator: ${failedOperatorCount}, Duplicates excluded: ${duplicateExcludedCount}, Matches: ${newDates.length}`);
        setClickedDates(newDates);
    }

    function handleSmartSearch() {
        if (!quoteData || quoteData.length === 0) return;

        let targetIndex = -1;
        for (let i = quoteData.length - 1; i >= 0; i--) {
            if (getLocalDate(quoteData[i].date) <= endDate) {
                targetIndex = i;
                break;
            }
        }

        if (targetIndex === -1) return;

        const newConditions = [];

        selectedIndicators.filter(i => i.location === 'lower').forEach(ind => {
            const id = ind.id;
            const values = [];

            for (let i = 0; i <= targetIndex; i++) {
                const row = quoteData[i];
                let val = undefined;
                if (row.upper && row.upper[id] !== undefined) val = row.upper[id];
                else if (row.lower && row.lower[id] !== undefined) val = row.lower[id];

                if (val !== undefined && val !== null) {
                    if (typeof val === 'object') {
                        if (val.value !== undefined) val = val.value;
                        else if (val.k !== undefined) val = val.k;
                        else if (Object.keys(val).length === 1) val = val[Object.keys(val)[0]];
                        else val = null;
                    }
                }
                if (val !== undefined && val !== null && !isNaN(val)) {
                    values.push(val);
                }
            }

            if (values.length > 10) {
                const mean = ss.mean(values);
                const stdDev = ss.standardDeviation(values);
                const todayVal = values[values.length - 1];

                const zScore = (todayVal - mean) / stdDev;

                if (Math.abs(zScore) >= smartSearchThreshold) {
                    newConditions.push({
                        type: 'value',
                        indicatorId: id,
                        operator: '',
                        value: todayVal.toFixed(2),
                        uid: Date.now() + Math.random()
                    });
                }
            }
        });
        setSearchConditions(newConditions);
    }

    function findSimilarDays() {
        if (!quoteData || quoteData.length === 0) return;
        if (clickedDates.length === 0) {
            alert("Please select at least one date to define the target profile.");
            return;
        }

        const indicators = selectedIndicators.map(i => i.id);
        if (indicators.length < 2) {
            alert("Need at least 2 indicators to calculate correlation.");
            return;
        }

        // Calculate target profile (average of selected dates)
        const targetProfile = {};
        indicators.forEach(id => targetProfile[id] = 0);

        let count = 0;
        clickedDates.forEach(clicked => {
            const d = getLocalDate(clicked.date);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const row = quoteDateMap.get(key)?.quote;
            if (row) {
                count++;
                indicators.forEach(id => {
                    let val = undefined;
                    if (row.upper && row.upper[id] !== undefined) val = row.upper[id];
                    else if (row.lower && row.lower[id] !== undefined) val = row.lower[id];

                    if (val !== undefined && val !== null) {
                        if (typeof val === 'object') {
                            if (val.value !== undefined) val = val.value;
                            else if (val.k !== undefined) val = val.k;
                            else val = Object.values(val)[0];
                        }
                        if (!isNaN(val)) targetProfile[id] += val;
                    }
                });
            }
        });

        if (count === 0) return;

        const targetVector = indicators.map(id => targetProfile[id] / count);

        // Search
        const matches = [];
        quoteData.forEach(row => {
            const rowVector = [];
            let valid = true;
            for (const id of indicators) {
                let val = undefined;
                if (row.upper && row.upper[id] !== undefined) val = row.upper[id];
                else if (row.lower && row.lower[id] !== undefined) val = row.lower[id];

                if (val !== undefined && val !== null) {
                    if (typeof val === 'object') {
                        if (val.value !== undefined) val = val.value;
                        else if (val.k !== undefined) val = val.k;
                        else val = Object.values(val)[0];
                    }
                }

                if (val === undefined || val === null || isNaN(val)) {
                    valid = false;
                    break;
                }
                rowVector.push(val);
            }

            if (valid) {
                try {
                    const corr = ss.sampleCorrelation(targetVector, rowVector);
                    if (corr > similarityThreshold) {
                        matches.push({ date: row.date, correlation: corr });
                    }
                } catch (e) {
                    // variance 0 etc
                }
            }
        });

        matches.sort((a, b) => b.correlation - a.correlation);
        const topMatches = matches.slice(0, 20);

        setClickedDates(prev => {
            const existing = new Set(prev.map(d => getLocalDate(d.date).getTime()));
            const toAdd = topMatches.filter(d => !existing.has(getLocalDate(d.date).getTime()));
            return [...prev, ...toAdd];
        });
    }

    function getChartData() {
        if (!tableData || tableData.length === 0 || !chartIndicator) return [];

        const rows = tableData.filter(r => r.date !== 'Average');
        const data = [['Value', 'Return']];

        const processed = rows.map(row => {
            let val = undefined;
            if (row[chartIndicator] !== undefined) val = row[chartIndicator];
            else if (row.upper && row.upper[chartIndicator] !== undefined) val = row.upper[chartIndicator];
            else if (row.lower && row.lower[chartIndicator] !== undefined) val = row.lower[chartIndicator];

            if (typeof val === 'object' && val !== null) {
                if (val.value !== undefined) val = val.value;
                else if (val.k !== undefined) val = val.k;
                else val = Object.values(val)[0];
            }
            return { val, ret: row[correlationTarget] };
        }).filter(item => item.val !== undefined && item.val !== null && !isNaN(item.val) && item.ret !== undefined && item.ret !== null);

        processed.sort((a, b) => a.val - b.val);
        processed.forEach(p => data.push([p.val.toFixed(2), p.ret]));
        return data;
    }

    const mappers = [
        {
            title: 'Date', field: 'date', formatter: (cell, row) => {
                if (row.date === 'Average') return <b>Average</b>;
                return (
                    <div className="sameline">
                        <span style={{ cursor: 'pointer', fontWeight: 'bold', marginRight: '5px', color: 'red' }} onClick={() => handleRemoveDate(row.date)}>X</span>
                        {tableHelpers.dateFormmatter(cell, row)}
                    </div>
                )
            }
        },
        { title: '1 Day', field: 'oneDayReturn', formatter: tableHelpers.percentChangeFormatter, width: 80, sorters: true },
        { title: '1 Week', field: 'oneWeekReturn', formatter: tableHelpers.percentChangeFormatter, width: 80, sorters: true },
        { title: '1 Month', field: 'oneMonthReturn', formatter: tableHelpers.percentChangeFormatter, width: 80, sorters: true },
        { title: '3 Months', field: 'threeMonthReturn', formatter: tableHelpers.percentChangeFormatter, width: 80, sorters: true },
        { title: 'Correlation', field: 'correlation', formatter: tableHelpers.decimalFormatter, width: 80, sorters: true }
    ];

    selectedIndicators.filter(i => i.location === 'lower').forEach(ind => {
        mappers.push({
            title: ind.id,
            field: ind.id,
            sorters: true,
            formatter: (cell, row) => {
                let val = undefined;
                if (cell !== undefined) val = cell;
                else if (row.lower && row.lower[ind.id] !== undefined) val = row.lower[ind.id];

                if (val !== undefined) {
                    if (typeof val === 'object') {
                        return Object.keys(val).map(k => `${k}: ${tableHelpers.decimalFormatter(val[k])}`).join(' ');
                    }
                    return tableHelpers.decimalFormatter(val);
                }
                return "";
            }

        });
    });

    const activeSearchIndicators = useMemo(() => {
        // 
        const activeIds = new Set(searchConditions
            .filter(c => c.value !== '' && c.value !== null && c.value !== undefined && c.operator !== '')
            .map(c => c.indicatorId));
        return selectedIndicators.filter(i => activeIds.has(i.id));
    }, [searchConditions, selectedIndicators]);

    const { tableData, summary } = useMemo(() => {
        if (!clickedDates || clickedDates.length === 0 || !quoteData || quoteData.length === 0) {
            return { tableData: [], summary: null };
        }

        const dataRows = clickedDates.map(clicked => {
            const d = getLocalDate(clicked.date);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const entry = quoteDateMap.get(key);

            if (entry) {
                const found = entry.quote;
                const foundIndex = entry.index;

                const oneDayLater = new Date(d);
                oneDayLater.setDate(d.getDate() + 1);
                const oneDayQuote = findQuoteForDate(oneDayLater, foundIndex);

                const oneWeekLater = new Date(d);
                oneWeekLater.setDate(d.getDate() + 7);
                const oneWeekQuote = findQuoteForDate(oneWeekLater, foundIndex);

                const oneMonthLater = new Date(d);
                oneMonthLater.setMonth(d.getMonth() + 1);
                const oneMonthQuote = findQuoteForDate(oneMonthLater, foundIndex);

                const threeMonthsLater = new Date(d);
                threeMonthsLater.setMonth(d.getMonth() + 3);
                const threeMonthsQuote = findQuoteForDate(threeMonthsLater, foundIndex);

                let row = {
                    ...found,
                    oneDayReturn: calculateReturn(found.close, oneDayQuote?.close),
                    oneWeekReturn: calculateReturn(found.close, oneWeekQuote?.close),
                    oneMonthReturn: calculateReturn(found.close, oneMonthQuote?.close),
                    threeMonthReturn: calculateReturn(found.close, threeMonthsQuote?.close),
                    oneDayDrawdown: calculateMaxDrawdown(foundIndex, oneDayLater, found.close),
                    oneWeekDrawdown: calculateMaxDrawdown(foundIndex, oneWeekLater, found.close),
                    oneMonthDrawdown: calculateMaxDrawdown(foundIndex, oneMonthLater, found.close),
                    threeMonthDrawdown: calculateMaxDrawdown(foundIndex, threeMonthsLater, found.close)
                };
                if (row.lower) {
                    row = { ...row, ...row.lower };
                }
                if (clicked.correlation) row.correlation = clicked.correlation;
                selectedIndicators.forEach(ind => {
                    row[`${ind.id}_1d`] = getIndicatorValue(oneDayQuote, ind.id);
                    row[`${ind.id}_1w`] = getIndicatorValue(oneWeekQuote, ind.id);
                    row[`${ind.id}_1m`] = getIndicatorValue(oneMonthQuote, ind.id);
                    row[`${ind.id}_3m`] = getIndicatorValue(threeMonthsQuote, ind.id);
                });
                return row;
            }

            return clicked;
        });

        let summaryObj = null;
        if (dataRows.length > 0) {
            summaryObj = { date: 'Average', lower: {}, futureIndicators: {} };
            let sums = { close: 0, oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
            let counts = { close: 0, oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
            let wins = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
            let winSums = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
            let lossSums = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
            let lossCounts = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
            let drawdownSums = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
            let indicatorSums = {};
            let futureIndicatorSums = {};

            activeSearchIndicators.forEach(ind => {
                futureIndicatorSums[ind.id] = {
                    '1d': { sum: 0, count: 0 },
                    '1w': { sum: 0, count: 0 },
                    '1m': { sum: 0, count: 0 },
                    '3m': { sum: 0, count: 0 }
                };
            });

            dataRows.forEach(row => {
                if (row.close !== undefined && row.close !== null) { sums.close += row.close; counts.close++; }
                if (row.oneDayReturn !== undefined && row.oneDayReturn !== null) {
                    sums.oneDayReturn += row.oneDayReturn; counts.oneDayReturn++;
                    if (row.oneDayReturn > 0) { wins.oneDayReturn++; winSums.oneDayReturn += row.oneDayReturn; }
                    else if (row.oneDayReturn < 0) { lossCounts.oneDayReturn++; lossSums.oneDayReturn += row.oneDayReturn; }
                    drawdownSums.oneDayReturn += row.oneDayDrawdown;
                }
                if (row.oneWeekReturn !== undefined && row.oneWeekReturn !== null) {
                    sums.oneWeekReturn += row.oneWeekReturn; counts.oneWeekReturn++;
                    if (row.oneWeekReturn > 0) { wins.oneWeekReturn++; winSums.oneWeekReturn += row.oneWeekReturn; }
                    else if (row.oneWeekReturn < 0) { lossCounts.oneWeekReturn++; lossSums.oneWeekReturn += row.oneWeekReturn; }
                    drawdownSums.oneWeekReturn += row.oneWeekDrawdown;
                }
                if (row.oneMonthReturn !== undefined && row.oneMonthReturn !== null) {
                    sums.oneMonthReturn += row.oneMonthReturn; counts.oneMonthReturn++;
                    if (row.oneMonthReturn > 0) { wins.oneMonthReturn++; winSums.oneMonthReturn += row.oneMonthReturn; }
                    else if (row.oneMonthReturn < 0) { lossCounts.oneMonthReturn++; lossSums.oneMonthReturn += row.oneMonthReturn; }
                    drawdownSums.oneMonthReturn += row.oneMonthDrawdown;
                }
                if (row.threeMonthReturn !== undefined && row.threeMonthReturn !== null) {
                    sums.threeMonthReturn += row.threeMonthReturn; counts.threeMonthReturn++;
                    if (row.threeMonthReturn > 0) { wins.threeMonthReturn++; winSums.threeMonthReturn += row.threeMonthReturn; }
                    else if (row.threeMonthReturn < 0) { lossCounts.threeMonthReturn++; lossSums.threeMonthReturn += row.threeMonthReturn; }
                    drawdownSums.threeMonthReturn += row.threeMonthDrawdown;
                }

                selectedIndicators.filter(i => i.location === 'lower').forEach(ind => {
                    let val = row.lower ? row.lower[ind.id] : undefined;
                    if (val !== undefined && val !== null) {
                        if (!indicatorSums[ind.id]) indicatorSums[ind.id] = { sum: 0, count: 0, isObject: false, objSum: {} };
                        if (typeof val === 'number') {
                            indicatorSums[ind.id].sum += val;
                            indicatorSums[ind.id].count++;
                        } else if (typeof val === 'object') {
                            indicatorSums[ind.id].isObject = true;
                            indicatorSums[ind.id].count++;
                            Object.keys(val).forEach(k => {
                                if (!indicatorSums[ind.id].objSum[k]) indicatorSums[ind.id].objSum[k] = 0;
                                indicatorSums[ind.id].objSum[k] += val[k];
                            });
                        }
                    }
                });
                activeSearchIndicators.forEach(ind => {
                    const id = ind.id;
                    if (row[`${id}_1d`] != null) { futureIndicatorSums[id]['1d'].sum += row[`${id}_1d`]; futureIndicatorSums[id]['1d'].count++; }
                    if (row[`${id}_1w`] != null) { futureIndicatorSums[id]['1w'].sum += row[`${id}_1w`]; futureIndicatorSums[id]['1w'].count++; }
                    if (row[`${id}_1m`] != null) { futureIndicatorSums[id]['1m'].sum += row[`${id}_1m`]; futureIndicatorSums[id]['1m'].count++; }
                    if (row[`${id}_3m`] != null) { futureIndicatorSums[id]['3m'].sum += row[`${id}_3m`]; futureIndicatorSums[id]['3m'].count++; }
                });
            });

            if (counts.close > 0) summaryObj.close = sums.close / counts.close;
            if (counts.oneDayReturn > 0) {
                summaryObj.oneDayReturn = sums.oneDayReturn / counts.oneDayReturn;
                summaryObj.oneDayWinRate = wins.oneDayReturn / counts.oneDayReturn * 100;
                if (wins.oneDayReturn > 0 && lossCounts.oneDayReturn > 0) summaryObj.oneDayWinLossRatio = (winSums.oneDayReturn / wins.oneDayReturn) / Math.abs(lossSums.oneDayReturn / lossCounts.oneDayReturn);
                summaryObj.oneDayMaxDrawdown = drawdownSums.oneDayReturn / counts.oneDayReturn;
            }
            if (counts.oneWeekReturn > 0) {
                summaryObj.oneWeekReturn = sums.oneWeekReturn / counts.oneWeekReturn;
                summaryObj.oneWeekWinRate = wins.oneWeekReturn / counts.oneWeekReturn * 100;
                if (wins.oneWeekReturn > 0 && lossCounts.oneWeekReturn > 0) summaryObj.oneWeekWinLossRatio = (winSums.oneWeekReturn / wins.oneWeekReturn) / Math.abs(lossSums.oneWeekReturn / lossCounts.oneWeekReturn);
                summaryObj.oneWeekMaxDrawdown = drawdownSums.oneWeekReturn / counts.oneWeekReturn;
            }
            if (counts.oneMonthReturn > 0) {
                summaryObj.oneMonthReturn = sums.oneMonthReturn / counts.oneMonthReturn;
                summaryObj.oneMonthWinRate = wins.oneMonthReturn / counts.oneMonthReturn * 100;
                if (wins.oneMonthReturn > 0 && lossCounts.oneMonthReturn > 0) summaryObj.oneMonthWinLossRatio = (winSums.oneMonthReturn / wins.oneMonthReturn) / Math.abs(lossSums.oneMonthReturn / lossCounts.oneMonthReturn);
                summaryObj.oneMonthMaxDrawdown = drawdownSums.oneMonthReturn / counts.oneMonthReturn;
            }
            if (counts.threeMonthReturn > 0) {
                summaryObj.threeMonthReturn = sums.threeMonthReturn / counts.threeMonthReturn;
                summaryObj.threeMonthWinRate = wins.threeMonthReturn / counts.threeMonthReturn * 100;
                if (wins.threeMonthReturn > 0 && lossCounts.threeMonthReturn > 0) summaryObj.threeMonthWinLossRatio = (winSums.threeMonthReturn / wins.threeMonthReturn) / Math.abs(lossSums.threeMonthReturn / lossCounts.threeMonthReturn);
                summaryObj.threeMonthMaxDrawdown = drawdownSums.threeMonthReturn / counts.threeMonthReturn;
            }

            Object.keys(indicatorSums).forEach(id => {
                const ind = indicatorSums[id];
                if (ind.count > 0) {
                    if (ind.isObject) {
                        summaryObj.lower[id] = {};
                        Object.keys(ind.objSum).forEach(k => summaryObj.lower[id][k] = ind.objSum[k] / ind.count);
                    } else {
                        summaryObj.lower[id] = ind.sum / ind.count;
                    }
                }
            });
            if (summaryObj.lower) {
                Object.assign(summaryObj, summaryObj.lower);
            }
            Object.keys(futureIndicatorSums).forEach(id => {
                summaryObj.futureIndicators[id] = {};
                ['1d', '1w', '1m', '3m'].forEach(period => {
                    if (futureIndicatorSums[id][period].count > 0) {
                        summaryObj.futureIndicators[id][period] = futureIndicatorSums[id][period].sum / futureIndicatorSums[id][period].count;
                    }
                });
            });
        }

        const finalTableData = summaryObj ? [summaryObj, ...dataRows] : dataRows;
        return { tableData: finalTableData, summary: summaryObj };
    }, [clickedDates, quoteData, quoteDateMap, selectedIndicators, activeSearchIndicators, calculateReturn, calculateMaxDrawdown, findQuoteForDate]);

    const matchedTrades = useMemo(() => {
        if (!simTransactions || simTransactions.length === 0) return [];

        const sortedTransactions = [...simTransactions].sort((a, b) => getLocalDate(a.date) - getLocalDate(b.date));
        const trades = [];
        const openLongs = [];
        const openShorts = [];

        sortedTransactions.forEach(t => {
            let qty = t.quantity;
            if (t.action === 'Buy') {
                openLongs.push({ date: t.date, price: t.price, quantity: qty });
            } else if (t.action === 'Short') {
                openShorts.push({ date: t.date, price: t.price, quantity: qty });
            } else if (t.action === 'Sell') {
                while (qty > 0 && openLongs.length > 0) {
                    const openTrade = openLongs[0];
                    const matchQty = Math.min(qty, openTrade.quantity);
                    trades.push({
                        purchase_date: openTrade.date,
                        purchase_price: openTrade.price,
                        sell_date: t.date,
                        sell_price: t.price,
                        quantity: matchQty,
                        is_short: false
                    });
                    openTrade.quantity -= matchQty;
                    qty -= matchQty;
                    if (openTrade.quantity === 0) openLongs.shift();
                }
            } else if (t.action === 'Cover') {
                while (qty > 0 && openShorts.length > 0) {
                    const openTrade = openShorts[0];
                    const matchQty = Math.min(qty, openTrade.quantity);
                    trades.push({
                        purchase_date: openTrade.date,
                        purchase_price: openTrade.price,
                        sell_date: t.date,
                        sell_price: t.price,
                        quantity: matchQty,
                        is_short: true
                    });
                    openTrade.quantity -= matchQty;
                    qty -= matchQty;
                    if (openTrade.quantity === 0) openShorts.shift();
                }
            }
        });

        openLongs.forEach(t => {
            trades.push({
                purchase_date: t.date,
                purchase_price: t.price,
                sell_date: null,
                sell_price: null,
                quantity: t.quantity,
                is_short: false
            });
        });
        openShorts.forEach(t => {
            trades.push({
                purchase_date: t.date,
                purchase_price: t.price,
                sell_date: null,
                sell_price: null,
                quantity: t.quantity,
                is_short: true
            });
        });
        return trades;
    }, [simTransactions]);

    const currentSimPrice = getCurrentSimPrice();
    const currentEquity = simBalance + (simPosition * currentSimPrice);
    const pl = currentEquity - 10000;
    const plPercent = pl / 10000 * 100;
    const unrealizedPl = simPosition !== 0 ? (currentSimPrice - simAvgPrice) * simPosition : 0;
    const unrealizedPlPercent = (simPosition !== 0 && simAvgPrice !== 0) ? (unrealizedPl / (Math.abs(simPosition) * simAvgPrice)) * 100 : 0;

    const randomSummary = useMemo(() => {
        if (!quoteData || quoteData.length === 0 || clickedDates.length === 0) return null;

        let summary = { date: 'Random', futureIndicators: {} };
        let sums = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
        let counts = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
        let wins = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
        let winSums = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
        let lossSums = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
        let lossCounts = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
        let drawdownSums = { oneDayReturn: 0, oneWeekReturn: 0, oneMonthReturn: 0, threeMonthReturn: 0 };
        let futureIndicatorSums = {};

        activeSearchIndicators.forEach(ind => {
            futureIndicatorSums[ind.id] = {
                '1d': { sum: 0, count: 0 },
                '1w': { sum: 0, count: 0 },
                '1m': { sum: 0, count: 0 },
                '3m': { sum: 0, count: 0 }
            };
        });

        const indices = new Set();
        const countToSelect = Math.min(100, quoteData.length);
        const selectedTimes = [];
        let attempts = 0;
        const maxAttempts = countToSelect * 50;

        const getExclusionMillis = () => {
            const day = 24 * 60 * 60 * 1000;
            if (excludeDuplicatesPeriod === 'oneDayReturn') return day;
            if (excludeDuplicatesPeriod === 'oneWeekReturn') return 7 * day;
            if (excludeDuplicatesPeriod === 'oneMonthReturn') return 30 * day;
            if (excludeDuplicatesPeriod === 'threeMonthReturn') return 90 * day;
            return 0;
        };
        const exclusionMillis = excludeDuplicates ? getExclusionMillis() : 0;

        while (indices.size < countToSelect && attempts < maxAttempts) {
            attempts++;
            const index = Math.floor(Math.random() * quoteData.length);
            if (indices.has(index)) continue;

            if (excludeDuplicates) {
                const candidateTime = (quoteDatesArray && quoteDatesArray[index] !== undefined)
                    ? quoteDatesArray[index]
                    : getLocalDate(quoteData[index].date).getTime();
                let conflict = false;
                for (const t of selectedTimes) {
                    if (Math.abs(candidateTime - t) < exclusionMillis) {
                        conflict = true;
                        break;
                    }
                }
                if (!conflict) {
                    indices.add(index);
                    selectedTimes.push(candidateTime);
                }
            } else {
                indices.add(index);
            }
        }

        const calcRet = (start, end) => {
            if (start && end) return (end - start) / start * 100;
            return null;
        };

        indices.forEach(index => {
            const found = quoteData[index];
            const d = getLocalDate(found.date);

            const oneDayLater = new Date(d); oneDayLater.setDate(d.getDate() + 1);
            const oneDayQuote = findQuoteForDate(oneDayLater, index);
            const oneDayReturn = calcRet(found.close, oneDayQuote?.close);

            const oneWeekLater = new Date(d); oneWeekLater.setDate(d.getDate() + 7);
            const oneWeekQuote = findQuoteForDate(oneWeekLater, index);
            const oneWeekReturn = calcRet(found.close, oneWeekQuote?.close);

            const oneMonthLater = new Date(d); oneMonthLater.setMonth(d.getMonth() + 1);
            const oneMonthQuote = findQuoteForDate(oneMonthLater, index);
            const oneMonthReturn = calcRet(found.close, oneMonthQuote?.close);

            const threeMonthsLater = new Date(d); threeMonthsLater.setMonth(d.getMonth() + 3);
            const threeMonthsQuote = findQuoteForDate(threeMonthsLater, index);
            const threeMonthReturn = calcRet(found.close, threeMonthsQuote?.close);

            const oneDayDrawdown = calculateMaxDrawdown(index, oneDayLater, found.close);
            const oneWeekDrawdown = calculateMaxDrawdown(index, oneWeekLater, found.close);
            const oneMonthDrawdown = calculateMaxDrawdown(index, oneMonthLater, found.close);
            const threeMonthDrawdown = calculateMaxDrawdown(index, threeMonthsLater, found.close);

            if (oneDayReturn !== null) {
                sums.oneDayReturn += oneDayReturn; counts.oneDayReturn++;
                if (oneDayReturn > 0) { wins.oneDayReturn++; winSums.oneDayReturn += oneDayReturn; }
                else if (oneDayReturn < 0) { lossCounts.oneDayReturn++; lossSums.oneDayReturn += oneDayReturn; }
                drawdownSums.oneDayReturn += oneDayDrawdown;
            }
            if (oneWeekReturn !== null) {
                sums.oneWeekReturn += oneWeekReturn; counts.oneWeekReturn++;
                if (oneWeekReturn > 0) { wins.oneWeekReturn++; winSums.oneWeekReturn += oneWeekReturn; }
                else if (oneWeekReturn < 0) { lossCounts.oneWeekReturn++; lossSums.oneWeekReturn += oneWeekReturn; }
                drawdownSums.oneWeekReturn += oneWeekDrawdown;
            }
            if (oneMonthReturn !== null) {
                sums.oneMonthReturn += oneMonthReturn; counts.oneMonthReturn++;
                if (oneMonthReturn > 0) { wins.oneMonthReturn++; winSums.oneMonthReturn += oneMonthReturn; }
                else if (oneMonthReturn < 0) { lossCounts.oneMonthReturn++; lossSums.oneMonthReturn += oneMonthReturn; }
                drawdownSums.oneMonthReturn += oneMonthDrawdown;
            }
            if (threeMonthReturn !== null) {
                sums.threeMonthReturn += threeMonthReturn; counts.threeMonthReturn++;
                if (threeMonthReturn > 0) { wins.threeMonthReturn++; winSums.threeMonthReturn += threeMonthReturn; }
                else if (threeMonthReturn < 0) { lossCounts.threeMonthReturn++; lossSums.threeMonthReturn += threeMonthReturn; }
                drawdownSums.threeMonthReturn += threeMonthDrawdown;
            }

            activeSearchIndicators.forEach(ind => {
                const id = ind.id;
                const v1d = getIndicatorValue(oneDayQuote, id);
                const v1w = getIndicatorValue(oneWeekQuote, id);
                const v1m = getIndicatorValue(oneMonthQuote, id);
                const v3m = getIndicatorValue(threeMonthsQuote, id);

                if (v1d != null) { futureIndicatorSums[id]['1d'].sum += v1d; futureIndicatorSums[id]['1d'].count++; }
                if (v1w != null) { futureIndicatorSums[id]['1w'].sum += v1w; futureIndicatorSums[id]['1w'].count++; }
                if (v1m != null) { futureIndicatorSums[id]['1m'].sum += v1m; futureIndicatorSums[id]['1m'].count++; }
                if (v3m != null) { futureIndicatorSums[id]['3m'].sum += v3m; futureIndicatorSums[id]['3m'].count++; }
            });
        });

        if (counts.oneDayReturn > 0) {
            summary.oneDayReturn = sums.oneDayReturn / counts.oneDayReturn;
            summary.oneDayWinRate = wins.oneDayReturn / counts.oneDayReturn * 100;
            if (wins.oneDayReturn > 0 && lossCounts.oneDayReturn > 0) summary.oneDayWinLossRatio = (winSums.oneDayReturn / wins.oneDayReturn) / Math.abs(lossSums.oneDayReturn / lossCounts.oneDayReturn);
            summary.oneDayMaxDrawdown = drawdownSums.oneDayReturn / counts.oneDayReturn;
        }
        if (counts.oneWeekReturn > 0) {
            summary.oneWeekReturn = sums.oneWeekReturn / counts.oneWeekReturn;
            summary.oneWeekWinRate = wins.oneWeekReturn / counts.oneWeekReturn * 100;
            if (wins.oneWeekReturn > 0 && lossCounts.oneWeekReturn > 0) summary.oneWeekWinLossRatio = (winSums.oneWeekReturn / wins.oneWeekReturn) / Math.abs(lossSums.oneWeekReturn / lossCounts.oneWeekReturn);
            summary.oneWeekMaxDrawdown = drawdownSums.oneWeekReturn / counts.oneWeekReturn;
        }
        if (counts.oneMonthReturn > 0) {
            summary.oneMonthReturn = sums.oneMonthReturn / counts.oneMonthReturn;
            summary.oneMonthWinRate = wins.oneMonthReturn / counts.oneMonthReturn * 100;
            if (wins.oneMonthReturn > 0 && lossCounts.oneMonthReturn > 0) summary.oneMonthWinLossRatio = (winSums.oneMonthReturn / wins.oneMonthReturn) / Math.abs(lossSums.oneMonthReturn / lossCounts.oneMonthReturn);
            summary.oneMonthMaxDrawdown = drawdownSums.oneMonthReturn / counts.oneMonthReturn;
        }
        if (counts.threeMonthReturn > 0) {
            summary.threeMonthReturn = sums.threeMonthReturn / counts.threeMonthReturn;
            summary.threeMonthWinRate = wins.threeMonthReturn / counts.threeMonthReturn * 100;
            if (wins.threeMonthReturn > 0 && lossCounts.threeMonthReturn > 0) summary.threeMonthWinLossRatio = (winSums.threeMonthReturn / wins.threeMonthReturn) / Math.abs(lossSums.threeMonthReturn / lossCounts.threeMonthReturn);
            summary.threeMonthMaxDrawdown = drawdownSums.threeMonthReturn / counts.threeMonthReturn;
        }

        Object.keys(futureIndicatorSums).forEach(id => {
            summary.futureIndicators[id] = {};
            ['1d', '1w', '1m', '3m'].forEach(period => {
                if (futureIndicatorSums[id][period].count > 0) {
                    summary.futureIndicators[id][period] = futureIndicatorSums[id][period].sum / futureIndicatorSums[id][period].count;
                }
            });
        });

        return summary;
    }, [quoteData, quoteDatesArray, clickedDates.length, excludeDuplicates, excludeDuplicatesPeriod, calculateMaxDrawdown, findQuoteForDate, activeSearchIndicators]);

    return (
        <div style={{ paddingBottom: isFooterExpanded ? '180px' : '120px' }}>
            <div className="sameline">
                <Dropdown width="150" onSelect={(e) => setSymbol(e)}>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        {symbol ? symbol : "Select symbol"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu  >
                        {symbols.sort().map(sym => {
                            return (<Dropdown.Item as='symbol' eventKey={sym} value={sym}>{sym}</Dropdown.Item>)
                        })}
                    </Dropdown.Menu>
                </Dropdown>
                &nbsp;&nbsp;
                <Dropdown width="150" onSelect={(e) => setComparisonSymbol(e === "None" ? null : e)}>
                    <Dropdown.Toggle variant="secondary" id="dropdown-comparison">
                        {comparisonSymbol ? `/ ${comparisonSymbol}` : " / None"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <Dropdown.Item as='symbol' eventKey={"None"} value={null}>None</Dropdown.Item>
                        {symbols.sort().map(sym => {
                            return (<Dropdown.Item as='symbol' eventKey={sym} value={sym}>{sym}</Dropdown.Item>)
                        })}
                    </Dropdown.Menu>
                </Dropdown>
                &nbsp;&nbsp;
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} />
                &nbsp;&nbsp;
                <ToggleButtonGroup
                    value={timeframe}
                    exclusive
                    color="primary"
                    size="small"
                    onChange={(e, val) => { if (val) setTimeframe(val) }}
                >
                    <ToggleButton value={1}>Daily</ToggleButton>
                    <ToggleButton value={2}>Weekly</ToggleButton>
                </ToggleButtonGroup>
                &nbsp;&nbsp;
                <input type="checkbox" checked={showVolume} onChange={(e) => setShowVolume(e.target.checked)} /> Volume
            </div>

            <br></br>
            <div className="sameline">
                <b>Saved Settings:</b>&nbsp;
                <select value={settingName} onChange={loadSetting}>
                    <option value="">Select...</option>
                    {savedSettings.map(s => <option key={s.idchart_settings} value={s.name}>{s.name}</option>)}
                </select>
                &nbsp;
                <input type="text" value={settingName} onChange={e => setSettingName(e.target.value)} placeholder="Setting Name" />
                &nbsp;
                <button className="btn btn-success btn-sm" onClick={saveSetting}>Save</button>
                &nbsp;
                <button className="btn btn-danger btn-sm" onClick={deleteSetting} disabled={!savedSettings.find(s => s.name === settingName)}>Delete</button>
                &nbsp;
                <ActionResult result={actionResult} />
            </div>
            <br></br>
            <IndicatorSelector indicators={indicatorOptions} selectedIndicators={selectedIndicators} setSelectedIndicators={setSelectedIndicators} patterns={patterns} />
            <div style={{ marginTop: '10px' }}>
                Lower Indicator SMA: <input type="number" min="1" style={{ width: '60px' }} value={lowerIndicatorSma} onChange={(e) => setLowerIndicatorSma(e.target.value)} />
            </div>
            <br></br>
            <CandleStickChart symbol={symbol} startDate={startDate.toISOString().split('T')[0]} endDate={endDate} showAveragePrice={true} indicators={selectedIndicators} width={props.chartWidth || props.width || (window.innerWidth * 0.6)} onDateClicked={handleDateClicked} trades={matchedTrades} annotations={clickedDates} timeframe={timeframe} showVolume={showVolume} data={quoteData} metaData={quoteMetaData} lowerIndicatorSma={lowerIndicatorSma ? Number(lowerIndicatorSma) : null} />
            <br></br>
            <div style={{ border: '1px solid #ccc', padding: '10px', margin: '10px 0' }}>
                <b>Search Criteria:</b>&nbsp;
                <input type="checkbox" checked={excludeDuplicates} onChange={(e) => setExcludeDuplicates(e.target.checked)} /> Exclude duplicates within...
                <select value={excludeDuplicatesPeriod} onChange={(e) => setExcludeDuplicatesPeriod(e.target.value)} disabled={!excludeDuplicates}>
                    <option value="oneDayReturn">1 Day</option>
                    <option value="oneWeekReturn">1 Week</option>
                    <option value="oneMonthReturn">1 Month</option>
                    <option value="threeMonthReturn">3 Months</option>
                </select>
                &nbsp;&nbsp;
                <button className="btn btn-primary" onClick={runSearch}>Search</button>
                &nbsp;&nbsp;
                <button className="btn btn-info" onClick={handleSmartSearch}>Smart Search</button>
                &nbsp;StdDevs: <input type="number" step="0.1" min="0.1" style={{ width: '50px' }} value={smartSearchThreshold} onChange={(e) => setSmartSearchThreshold(Number(e.target.value))} />
                <br></br>
                <br></br>
                <select value={searchIndicatorToAdd} onChange={(e) => setSearchIndicatorToAdd(e.target.value)}>
                    <option value="">Select Indicator</option>
                    {selectedIndicators.filter(i => i.location === 'lower').sort((a, b) => a.id.localeCompare(b.id)).map(i => <option key={i.id} value={i.id}>{i.id}</option>)}
                </select>
                &nbsp;
                <select value={searchOperatorToAdd} onChange={(e) => setSearchOperatorToAdd(e.target.value)}>
                    <option value="<">&lt;</option>
                    <option value=">">&gt;</option>
                    <option value="=">=</option>
                    <option value="<>">&lt;&gt;</option>
                </select>
                &nbsp;
                <input type="number" style={{ width: '60px' }} value={searchValueToAdd} onChange={(e) => setSearchValueToAdd(e.target.value)} />
                &nbsp;
                <button className="btn btn-secondary btn-sm" onClick={addSearchCondition}>Add Condition</button>
                <br />
                <br />
                <select value={searchIndicator1ToAdd} onChange={(e) => setSearchIndicator1ToAdd(e.target.value)}>
                    <option value="">Select Upper Indicator 1</option>
                    {selectedIndicators.filter(i => i.location === 'upper').sort((a, b) => a.id.localeCompare(b.id)).map(i => <option key={i.id} value={i.id}>{i.id}</option>)}
                </select>
                &nbsp;
                <select value={searchIndicatorOperatorToAdd} onChange={(e) => setSearchIndicatorOperatorToAdd(e.target.value)}>
                    <option value="<">&lt;</option>
                    <option value=">">&gt;</option>
                    <option value="=">=</option>
                    <option value="<>">&lt;&gt;</option>
                </select>
                &nbsp;
                <select value={searchIndicator2ToAdd} onChange={(e) => setSearchIndicator2ToAdd(e.target.value)}>
                    <option value="">Select Upper Indicator 2</option>
                    {selectedIndicators.filter(i => i.location === 'upper').sort((a, b) => a.id.localeCompare(b.id)).map(i => <option key={i.id} value={i.id}>{i.id}</option>)}
                </select>
                &nbsp;
                <button className="btn btn-secondary btn-sm" onClick={addIndicatorCompareCondition}>Add Condition</button>

                {searchConditions.map(cond => (
                    <div key={cond.uid} style={{ marginTop: '5px' }}>
                        {cond.type === 'indicator' ? (
                            <>
                                <span style={{ display: 'inline-block', width: '150px' }}>{cond.indicator1Id}</span>
                                <select value={cond.operator} onChange={(e) => updateSearchCondition(cond.uid, 'operator', e.target.value)}>
                                    <option value=""></option>
                                    <option value="<">&lt;</option>
                                    <option value=">">&gt;</option>
                                    <option value="=">=</option>
                                    <option value="<>">&lt;&gt;</option>
                                </select>
                                &nbsp;
                                <span style={{ display: 'inline-block', width: '150px' }}>{cond.indicator2Id}</span>
                            </>
                        ) : (
                            <>
                                <span style={{ display: 'inline-block', width: '150px' }}>{cond.indicatorId}</span>
                                <select value={cond.operator} onChange={(e) => updateSearchCondition(cond.uid, 'operator', e.target.value)}>
                                    <option value=""></option>
                                    <option value="<">&lt;</option>
                                    <option value=">">&gt;</option>
                                    <option value="=">=</option>
                                    <option value="<>">&lt;&gt;</option>
                                </select>
                                &nbsp;
                                <input type="number" style={{ width: '60px' }} value={cond.value} onChange={(e) => updateSearchCondition(cond.uid, 'value', e.target.value)} />
                            </>
                        )}
                        <span style={{ cursor: 'pointer', fontWeight: 'bold', marginLeft: '5px', color: 'red' }} onClick={() => removeSearchCondition(cond.uid)}>X</span>
                    </div>
                ))}
            </div>
            {summary && <div style={{ marginBottom: '10px' }}>
                <b>Average Returns:</b>
                <table className="table table-striped table-condensed" style={{ width: 'auto', border: '1px solid #ccc' }}>
                    <thead>
                        <tr>
                            <th style={{ padding: '5px 10px' }}>Count</th>
                            <th style={{ padding: '5px 10px' }}>% of Total</th>
                            <th style={{ padding: '5px 10px' }}>1 Day</th>
                            <th style={{ padding: '5px 10px' }}>1 Week</th>
                            <th style={{ padding: '5px 10px' }}>1 Month</th>
                            <th style={{ padding: '5px 10px' }}>3 Months</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ padding: '5px 10px' }}>{clickedDates.length}</td>
                            <td style={{ padding: '5px 10px' }}>{quoteData.length > 0 ? tableHelpers.percentFormatter(clickedDates.length / quoteData.length * 100) : ''}</td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(summary.oneDayReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(summary.oneDayWinRate)}</span>
                                {summary.oneDayWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{summary.oneDayWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(summary.oneDayMaxDrawdown)}</span>
                            </td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(summary.oneWeekReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(summary.oneWeekWinRate)}</span>
                                {summary.oneWeekWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{summary.oneWeekWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(summary.oneWeekMaxDrawdown)}</span>
                            </td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(summary.oneMonthReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(summary.oneMonthWinRate)}</span>
                                {summary.oneMonthWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{summary.oneMonthWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(summary.oneMonthMaxDrawdown)}</span>
                            </td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(summary.threeMonthReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(summary.threeMonthWinRate)}</span>
                                {summary.threeMonthWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{summary.threeMonthWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(summary.threeMonthMaxDrawdown)}</span>
                            </td>
                        </tr>
                        {activeSearchIndicators.map(ind => (
                            <tr key={ind.id}>
                                <td style={{ padding: '5px 10px' }} colSpan={2}>{ind.id}</td>
                                <td style={{ padding: '5px 10px' }}>{summary.futureIndicators[ind.id]?.['1d'] ? tableHelpers.decimalFormatter(summary.futureIndicators[ind.id]['1d']) : ''}</td>
                                <td style={{ padding: '5px 10px' }}>{summary.futureIndicators[ind.id]?.['1w'] ? tableHelpers.decimalFormatter(summary.futureIndicators[ind.id]['1w']) : ''}</td>
                                <td style={{ padding: '5px 10px' }}>{summary.futureIndicators[ind.id]?.['1m'] ? tableHelpers.decimalFormatter(summary.futureIndicators[ind.id]['1m']) : ''}</td>
                                <td style={{ padding: '5px 10px' }}>{summary.futureIndicators[ind.id]?.['3m'] ? tableHelpers.decimalFormatter(summary.futureIndicators[ind.id]['3m']) : ''}</td>
                            </tr>
                        ))}
                        {randomSummary && <tr>
                            <td style={{ padding: '5px 10px' }}>Random</td>
                            <td style={{ padding: '5px 10px' }}></td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(randomSummary.oneDayReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(randomSummary.oneDayWinRate)}</span>
                                {randomSummary.oneDayWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{randomSummary.oneDayWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(randomSummary.oneDayMaxDrawdown)}</span>
                            </td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(randomSummary.oneWeekReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(randomSummary.oneWeekWinRate)}</span>
                                {randomSummary.oneWeekWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{randomSummary.oneWeekWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(randomSummary.oneWeekMaxDrawdown)}</span>
                            </td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(randomSummary.oneMonthReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(randomSummary.oneMonthWinRate)}</span>
                                {randomSummary.oneMonthWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{randomSummary.oneMonthWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(randomSummary.oneMonthMaxDrawdown)}</span>
                            </td>
                            <td style={{ padding: '5px 10px' }}>
                                {tableHelpers.percentChangeFormatter(randomSummary.threeMonthReturn)}
                                <br></br><span style={{ fontSize: '11px' }}>{tableHelpers.percentFormatter(randomSummary.threeMonthWinRate)}</span>
                                {randomSummary.threeMonthWinLossRatio ? <><br></br><span style={{ fontSize: '11px' }}>{randomSummary.threeMonthWinLossRatio.toFixed(2)}</span></> : null}
                                <br></br><span style={{ fontSize: '11px', color: 'red' }}>{tableHelpers.percentFormatter(randomSummary.threeMonthMaxDrawdown)}</span>
                            </td>
                        </tr>}
                        {randomSummary && activeSearchIndicators.map(ind => (
                            <tr key={`rnd_${ind.id}`}>
                                <td style={{ padding: '5px 10px' }} colSpan={2}>{ind.id}</td>
                                <td style={{ padding: '5px 10px' }}>{randomSummary.futureIndicators[ind.id]?.['1d'] ? tableHelpers.decimalFormatter(randomSummary.futureIndicators[ind.id]['1d']) : ''}</td>
                                <td style={{ padding: '5px 10px' }}>{randomSummary.futureIndicators[ind.id]?.['1w'] ? tableHelpers.decimalFormatter(randomSummary.futureIndicators[ind.id]['1w']) : ''}</td>
                                <td style={{ padding: '5px 10px' }}>{randomSummary.futureIndicators[ind.id]?.['1m'] ? tableHelpers.decimalFormatter(randomSummary.futureIndicators[ind.id]['1m']) : ''}</td>
                                <td style={{ padding: '5px 10px' }}>{randomSummary.futureIndicators[ind.id]?.['3m'] ? tableHelpers.decimalFormatter(randomSummary.futureIndicators[ind.id]['3m']) : ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>}
            <div style={{ border: '1px solid #ccc', padding: '10px', margin: '10px 0' }}>
                <b>Correlation Analysis:</b>&nbsp;
                <select value={correlationTarget} onChange={(e) => setCorrelationTarget(e.target.value)}>
                    <option value="oneDayReturn">1 Day Return</option>
                    <option value="oneWeekReturn">1 Week Return</option>
                    <option value="oneMonthReturn">1 Month Return</option>
                    <option value="threeMonthReturn">3 Month Return</option>
                </select>
                &nbsp;&nbsp;
                <button className="btn btn-primary" onClick={analyzeCorrelations}>Analyze</button>
                &nbsp;&nbsp;
                Diff StdDevs: <input type="number" step="0.1" min="0.1" style={{ width: '50px' }} value={correlationStdDevThreshold} onChange={(e) => setCorrelationStdDevThreshold(Number(e.target.value))} />
                {correlationResults && Array.isArray(correlationResults) && (
                    <div style={{ marginTop: '10px' }}>
                        <RichTable data={correlationResults} mappers={[
                            { title: 'Indicator', field: 'indicator', sorters: true },
                            { title: 'Overall Corr', field: 'overallCorrelation', formatter: tableHelpers.decimalFormatter, sorters: true },
                            { title: 'Overall Avg', field: 'overallAvg', formatter: tableHelpers.decimalFormatter, sorters: true },
                            { title: 'Current Value', field: 'currentValue', formatter: tableHelpers.decimalFormatter, sorters: true },
                            { title: 'Win Corr', field: 'winCorrelation', formatter: tableHelpers.decimalFormatter, sorters: true },
                            {
                                title: 'Win Avg', field: 'winAvg', formatter: (cell, row) => {
                                    const isSignificant = row.winAvg != null && row.lossAvg != null && row.stdDev && Math.abs(row.winAvg - row.lossAvg) > (row.stdDev * correlationStdDevThreshold);
                                    return <div style={isSignificant ? { backgroundColor: 'lightgreen' } : {}}>{tableHelpers.decimalFormatter(cell)}</div>
                                }, sorters: true
                            },
                            { title: 'Win Count', field: 'winCount', sorters: true },
                            { title: 'Loss Corr', field: 'lossCorrelation', formatter: tableHelpers.decimalFormatter, sorters: true },
                            {
                                title: 'Loss Avg', field: 'lossAvg', formatter: (cell, row) => {
                                    const isSignificant = row.winAvg != null && row.lossAvg != null && row.stdDev && Math.abs(row.winAvg - row.lossAvg) > (row.stdDev * correlationStdDevThreshold);
                                    return <div style={isSignificant ? { backgroundColor: 'lightgreen' } : {}}>{tableHelpers.decimalFormatter(cell)}</div>
                                }, sorters: true
                            },
                            { title: 'Loss Count', field: 'lossCount', sorters: true },
                        ]} className="table-striped table-hover table-condensed" />
                    </div>
                )}
            </div>
            <div style={{ marginBottom: '10px', border: '1px solid #ccc', padding: '10px' }}>
                <b>Chart Results:</b>&nbsp;
                <select value={chartIndicator} onChange={(e) => setChartIndicator(e.target.value)}>
                    <option value="">Select Indicator</option>
                    {selectedIndicators.map(i => <option key={i.id} value={i.id}>{i.id}</option>)}
                </select>
                {chartIndicator && getChartData().length > 1 && (
                    <Chart
                        chartType="ColumnChart"
                        width="100%"
                        height="400px"
                        data={getChartData()}
                        options={{ legend: { position: "none" }, hAxis: { title: chartIndicator }, vAxis: { title: correlationTarget } }}
                    />
                )}
            </div>
            <div style={{ border: '1px solid #ccc', padding: '10px', margin: '10px 0' }}>
                <b>Trade Signals:</b>&nbsp;
                <button className="btn btn-primary" onClick={handleGetSignals}>Get Signals</button>
                {tradeSignals && tradeSignals.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                        <RichTable
                            data={tradeSignals}
                            mappers={[
                                { title: 'Algo', field: 'display_name' },
                                { title: 'Symbol', field: 'trade_symbol' },
                                { title: 'Action', field: 'action' },
                                { title: 'Profit', field: 'profit', formatter: tableHelpers.percentFormatter }
                            ]}
                            className="table-striped table-hover table-condensed"
                            pageSize={10}
                        />
                    </div>
                )}
            </div>

            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <b>Raw Data ({clickedDates.length})</b>
                </AccordionSummary>
                <AccordionDetails>
                    <button className="btn btn-secondary" onClick={handleClearDates}>Clear Dates</button>
                    &nbsp;&nbsp;<button className="btn btn-secondary" onClick={handleCopyCSV}>Copy CSV</button>
                    <RichTable data={tableData} mappers={mappers} className="table-striped table-hover table-condensed" rowClassName={(row) => row.date === 'Average' ? 'font-weight-bold' : ''} />
                </AccordionDetails>
            </Accordion>
            <br></br>
            <div className="sameline">
                <button className="btn btn-primary" onClick={findSimilarDays}>Find Similar Days</button>
                &nbsp;&nbsp;Threshold: <input type="number" step="0.01" min="0" max="1" style={{ width: '60px' }} value={similarityThreshold} onChange={(e) => setSimilarityThreshold(Number(e.target.value))} />
            </div>
            <div style={{ position: 'fixed', bottom: 56, left: 0, width: '100%', backgroundColor: '#f9f9f9', borderTop: '1px solid #ccc', padding: '10px', zIndex: 200, boxShadow: '0 -2px 5px rgba(0,0,0,0.1)' }}>
                <div style={{ position: 'absolute', top: '5px', left: '5px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setIsFooterExpanded(!isFooterExpanded)}>
                        {isFooterExpanded ? 'Hide' : 'Show'}
                    </button>
                </div>
                {isFooterExpanded ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '5px' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => adjustDate(-1, 0)}>&lt;&lt;</button>&nbsp;
                            <button className="btn btn-secondary btn-sm" onClick={() => adjustDate(0, -1)}>&lt;</button>&nbsp;
                            <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} popperPlacement="top-start" />
                            &nbsp;<button className="btn btn-secondary btn-sm" onClick={() => adjustDate(0, 1)}>&gt;</button>
                            &nbsp;<button className="btn btn-secondary btn-sm" onClick={() => adjustDate(1, 0)}>&gt;&gt;</button>
                            &nbsp;&nbsp;
                            <button className="btn btn-info btn-sm" onClick={handleRandomDate}>Random Date</button>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                            <b>Sim ($10k):</b>&nbsp;
                            <button className="btn btn-success btn-sm" onClick={handleSimBuy} disabled={simPosition >= 0 && simBalance < currentSimPrice}>Buy {simPosition < 0 ? "(Cover)" : ""}</button>
                            &nbsp;
                            <button className="btn btn-danger btn-sm" onClick={handleSimSell} disabled={simPosition < 0}>Sell {simPosition === 0 ? "(Short)" : ""}</button>
                            &nbsp;&nbsp;
                            <span style={{ fontSize: '0.9em' }}>Price: {tableHelpers.moneyFormatter(currentSimPrice)}</span>
                            &nbsp;|&nbsp;
                            <span style={{ fontSize: '0.9em' }}>Qty: <input type="number" style={{ width: '50px' }} value={simQuantity} onChange={(e) => setSimQuantity(e.target.value)} /></span>
                            &nbsp;|&nbsp;
                            <span style={{ fontSize: '0.9em' }}>Pos: {simPosition}</span>
                            &nbsp;|&nbsp;
                            <span style={{ fontSize: '0.9em' }}>Bal: {tableHelpers.moneyFormatter(simBalance)}</span>
                            &nbsp;|&nbsp;
                            <span style={{ fontSize: '0.9em' }}>Eq: {tableHelpers.moneyFormatter(currentEquity)}</span>
                            &nbsp;|&nbsp;
                            <span style={{ fontSize: '0.9em' }}>P/L: <span style={{ color: pl >= 0 ? 'green' : 'red' }}>{tableHelpers.moneyFormatter(pl)} ({tableHelpers.percentFormatter(plPercent)})</span></span>
                            {simPosition !== 0 && <span style={{ fontSize: '0.9em' }}>&nbsp;|&nbsp;Unr: <span style={{ color: unrealizedPl >= 0 ? 'green' : 'red' }}>{tableHelpers.moneyFormatter(unrealizedPl)} ({tableHelpers.percentFormatter(unrealizedPlPercent)})</span></span>}
                            &nbsp;&nbsp;
                            <Tooltip id="tooltip-history" text={'History'} width={'500px'} position="top">
                                <>
                                    <RichTable
                                        data={[...simTransactions].sort((a, b) => getLocalDate(b.date) - getLocalDate(a.date))}
                                        mappers={[
                                            { title: 'Date', field: 'date' },
                                            { title: 'Action', field: 'action' },
                                            { title: 'Qty', field: 'quantity' },
                                            { title: 'Price', field: 'price', formatter: tableHelpers.moneyFormatter }
                                        ]}
                                        className="table-striped table-hover table-condensed"
                                        pageSize={5}
                                    />
                                </>
                            </Tooltip>
                            &nbsp;&nbsp;
                            <button className="btn btn-secondary btn-sm" onClick={resetSim}>Reset</button>
                        </div>
                    </>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <span style={{ marginRight: '15px' }}>Date: {endDate.toLocaleDateString()}</span>
                        <b>Sim:</b>&nbsp;
                        <span style={{ fontSize: '0.9em' }}>P/L: <span style={{ color: pl >= 0 ? 'green' : 'red' }}>{tableHelpers.moneyFormatter(pl)} ({tableHelpers.percentFormatter(plPercent)})</span></span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StockResearch;